/**
 * Parsing and validation of the `Elenco Spese` category hierarchy.
 *
 * The sheet is a flat list in column A: a fully upper-case line is a group header, and the lines
 * that follow it are its leaf categories (docs/workbook-analysis.md §3). Labels are kept EXACTLY
 * as in Excel — typos included — because the monthly sheets match them by text (SUMIFS).
 * Nothing here invents, renames, trims or re-cases a category.
 */
import { sha256Hex } from "@/lib/hash";

export class CategoryHierarchyError extends Error {
  override readonly name = "CategoryHierarchyError";
  constructor(
    message: string,
    readonly problems: string[] = [],
  ) {
    super(problems.length ? `${message}:\n- ${problems.join("\n- ")}` : message);
  }
}

export interface SourceRow {
  row: number;
  value: string;
}

export interface ParsedLeaf {
  label: string;
  excelRow: number;
  sortOrder: number;
}

export interface ParsedGroup {
  label: string;
  excelRow: number;
  sortOrder: number;
  leaves: ParsedLeaf[];
}

/** Non-blocking observation about the source list. `message` is English, for logs and the CLI. */
export type HierarchyWarning =
  | { code: "whitespace"; row: number; label: string; message: string }
  | { code: "empty-group"; row: number; label: string; message: string }
  | { code: "case-collision-with-group"; row: number; label: string; groupRow: number; groupLabel: string; message: string };

export interface ParsedHierarchy {
  groups: ParsedGroup[];
  leafCount: number;
  /** Non-blocking observations (e.g. a leaf that differs from a group only by case). */
  warnings: HierarchyWarning[];
}

/** A line is a group header when it contains letters and none of them is lower-case. */
export function isGroupHeader(value: string): boolean {
  return /\p{L}/u.test(value) && value === value.toLocaleUpperCase("it-IT");
}

/** sha256 of the ordered source values; identical input → identical hash (change detection). */
export function hashSourceRows(rows: SourceRow[]): Promise<string> {
  return sha256Hex(JSON.stringify(rows.map((r) => [r.row, r.value])));
}

export function parseCategoryHierarchy(rows: SourceRow[]): ParsedHierarchy {
  const problems: string[] = [];
  const warnings: HierarchyWarning[] = [];
  const groups: ParsedGroup[] = [];
  let current: ParsedGroup | undefined;
  let leafOrder = 0;

  for (const { row, value } of rows) {
    if (value.trim() === "") continue;
    if (value !== value.trim()) {
      warnings.push({ code: "whitespace", row, label: value, message: `Row ${row}: label has leading/trailing spaces: ${JSON.stringify(value)}` });
    }

    if (isGroupHeader(value)) {
      current = { label: value, excelRow: row, sortOrder: groups.length + 1, leaves: [] };
      groups.push(current);
    } else if (!current) {
      problems.push(`Row ${row}: category "${value}" appears before any group header`);
    } else {
      current.leaves.push({ label: value, excelRow: row, sortOrder: ++leafOrder });
    }
  }

  if (groups.length === 0) problems.push("No group headers found");

  const seenGroups = new Map<string, number>();
  const seenLeavesExact = new Map<string, number>();
  const seenLeavesFolded = new Map<string, { label: string; row: number }>();
  for (const group of groups) {
    if (seenGroups.has(group.label)) {
      problems.push(`Row ${group.excelRow}: duplicate group "${group.label}" (also row ${seenGroups.get(group.label)})`);
    }
    seenGroups.set(group.label, group.excelRow);
    if (group.leaves.length === 0) {
      warnings.push({
        code: "empty-group",
        row: group.excelRow,
        label: group.label,
        message: `Row ${group.excelRow}: group "${group.label}" has no categories`,
      });
    }

    for (const leaf of group.leaves) {
      const exact = seenLeavesExact.get(leaf.label);
      if (exact !== undefined) {
        problems.push(`Row ${leaf.excelRow}: duplicate category "${leaf.label}" (also row ${exact})`);
        continue;
      }
      seenLeavesExact.set(leaf.label, leaf.excelRow);
      // SUMIFS compares text case-insensitively: two leaves differing only by case would be summed together.
      const folded = leaf.label.toLocaleLowerCase("it-IT");
      const clash = seenLeavesFolded.get(folded);
      if (clash) {
        problems.push(
          `Row ${leaf.excelRow}: category "${leaf.label}" differs only by case from "${clash.label}" (row ${clash.row})`,
        );
      }
      seenLeavesFolded.set(folded, { label: leaf.label, row: leaf.excelRow });
    }
  }

  for (const group of groups) {
    const clash = seenLeavesFolded.get(group.label.toLocaleLowerCase("it-IT"));
    if (clash) {
      warnings.push({
        code: "case-collision-with-group",
        row: clash.row,
        label: clash.label,
        groupRow: group.excelRow,
        groupLabel: group.label,
        message:
          `Category "${clash.label}" (row ${clash.row}) differs only by case from group "${group.label}" (row ${group.excelRow}); ` +
          "Excel SUMIFS treats them as equal, so a group name must never be used as an expense category",
      });
    }
  }

  if (problems.length) throw new CategoryHierarchyError("Invalid category list in Elenco Spese", problems);

  return {
    groups,
    leafCount: groups.reduce((n, g) => n + g.leaves.length, 0),
    warnings,
  };
}

/**
 * Cross-check (conflict C7): the monthly sheets encode group membership a second time, by the
 * table block a label sits in. Scans the label columns of one monthly sheet and verifies that
 * every leaf appears exactly once, under the same group as in Elenco Spese.
 */
export function verifyAgainstMonthlySheet(
  hierarchy: ParsedHierarchy,
  labelColumns: SourceRow[][],
  sheetName: string,
): void {
  const groupOfLeaf = new Map<string, string>();
  for (const g of hierarchy.groups) for (const l of g.leaves) groupOfLeaf.set(l.label, g.label);
  const groupLabels = new Set(hierarchy.groups.map((g) => g.label));

  const found = new Map<string, string[]>(); // leaf → groups it was found under
  for (const column of labelColumns) {
    let currentGroup: string | undefined;
    for (const { value } of column) {
      if (groupLabels.has(value)) {
        currentGroup = value;
      } else if (groupOfLeaf.has(value)) {
        found.set(value, [...(found.get(value) ?? []), currentGroup ?? "(none)"]);
      }
    }
  }

  const problems: string[] = [];
  for (const [leaf, group] of groupOfLeaf) {
    const where = found.get(leaf);
    if (!where) problems.push(`"${leaf}" (${group}) is missing from ${sheetName}`);
    else if (where.length > 1) problems.push(`"${leaf}" appears ${where.length} times in ${sheetName}`);
    else if (where[0] !== group) problems.push(`"${leaf}" is under ${where[0]} in ${sheetName} but under ${group} in Elenco Spese`);
  }
  if (problems.length) {
    throw new CategoryHierarchyError(`Elenco Spese and ${sheetName} disagree`, problems);
  }
}
